<script setup lang="ts">
import { computed, ref } from 'vue'
import { useRoute } from 'vue-router'
import { onClickOutside } from '@vueuse/core'
import { Check, ChevronDown } from 'lucide-vue-next'
import { useModal } from '@/modules/shared/composables/use-modal'
import EntityLayout from '@/modules/investing/components/entities/entity-layout.vue'
import CloseSlideOut from '@/modules/investing/components/overview/close-slide-out.vue'
import ExitSlideOut from '@/modules/investing/components/overview/exit-slide-out.vue'
import OverviewSkeleton from '@/modules/investing/components/overview/overview-skeleton.vue'
import type { CloseReadiness, ExitReadiness, SpvExitType } from '@/modules/investing/types'

import { VButton, VConfirmDialog } from '@/modules/shared/components'

const props = withDefaults(
  defineProps<{
    entityType: 'spv' | 'fund'
    isRaising: boolean
    isClosed: boolean
    isExited: boolean
    isAdmin: boolean
    isSiteAdmin: boolean
    lifecycleState: 'raising' | 'closed' | 'exited'
    onReopen: () => Promise<void>
    onReclose: () => Promise<void>
    onCloseSubmit: (payload: { closing_documents: File[] }) => Promise<void>
    onExitSubmit: (payload: { exit_date: string; exit_type: SpvExitType; exit_documents: File[] }) => Promise<void>
    fetchExitReadiness: () => Promise<ExitReadiness>
    fetchCloseReadiness: () => Promise<CloseReadiness>
    completeInvestmentHref: string | null
    skeleton?: boolean
  }>(),
  { skeleton: false },
)

///////////////////////////////////////////////////////////////////////////////
// Labels
///////////////////////////////////////////////////////////////////////////////

const entityLabel = computed(() => (props.entityType === 'fund' ? 'Fund' : 'SPV'))
const investorLabel = computed(() => (props.entityType === 'fund' ? 'LPs' : 'investors'))

///////////////////////////////////////////////////////////////////////////////
// Edit route
///////////////////////////////////////////////////////////////////////////////

const route = useRoute()
const { slug, entity_id } = route.params as { slug: string; entity_id: string }
const editRoute = computed(() => ({
  name: props.entityType === 'fund' ? 'investing.fund-edit' : 'investing.spv-edit',
  params: { slug, entity_id },
}))

///////////////////////////////////////////////////////////////////////////////
// Stage pill dropdown
///////////////////////////////////////////////////////////////////////////////

type LifecycleState = 'raising' | 'closed' | 'exited'

const stageMenuOpen = ref(false)
const stageMenuRef = ref()

onClickOutside(stageMenuRef, () => (stageMenuOpen.value = false))

const stageLabel = computed(() => {
  switch (props.lifecycleState) {
    case 'raising':
      return 'Raising'
    case 'closed':
      return 'Closed'
    case 'exited':
      return 'Exited'
    default:
      return '—'
  }
})

const stages = computed(() => {
  const isFund = props.entityType === 'fund'
  const all: { key: LifecycleState; label: string }[] = [
    { key: 'raising', label: 'Raising' },
    { key: 'closed', label: 'Closed' },
    ...(!isFund ? [{ key: 'exited' as const, label: 'Exited' }] : []),
  ]
  return all
})

const stageIndex = (key: LifecycleState) => stages.value.findIndex((s) => s.key === key)

const isStageCompleted = (idx: number) => idx < stageIndex(props.lifecycleState)
const isStageActive = (idx: number) => stages.value[idx].key === props.lifecycleState
const isStageFuture = (idx: number) => idx > stageIndex(props.lifecycleState)

const canSelectStage = (key: LifecycleState): boolean => {
  if (key === props.lifecycleState) return false
  if (!props.isAdmin) return false

  // Cannot jump from raising to exited
  if (props.lifecycleState === 'raising' && key === 'exited') return false

  // Only site admins can re-close (exited → closed)
  if (props.lifecycleState === 'exited' && key === 'closed' && !props.isSiteAdmin) return false

  // Cannot go from exited/closed back to raising without reopen permission
  if (key === 'raising' && props.lifecycleState !== 'raising') return true

  return true
}

const selectStage = (key: LifecycleState) => {
  if (!canSelectStage(key)) return
  stageMenuOpen.value = false

  if (props.lifecycleState === 'raising' && key === 'closed') {
    handleCloseClick()
  } else if (props.lifecycleState === 'closed' && key === 'exited') {
    exitSlideOut.open()
  } else if (props.lifecycleState === 'closed' && key === 'raising') {
    reopenModal.open()
  } else if (props.lifecycleState === 'exited' && key === 'closed') {
    recloseModal.open()
  }
}

///////////////////////////////////////////////////////////////////////////////
// Modal stores
///////////////////////////////////////////////////////////////////////////////

const closeSlideOut = useModal()
const closeFundModal = useModal()
const exitSlideOut = useModal()
const reopenModal = useModal()
const recloseModal = useModal()

const submitting = ref(false)

const handleCloseClick = () => {
  if (props.entityType === 'fund') {
    closeFundModal.open()
  } else {
    closeSlideOut.open()
  }
}

const handleCloseFund = async () => {
  if (submitting.value) return
  submitting.value = true
  try {
    await props.onCloseSubmit({ closing_documents: [] })
    closeFundModal.close()
  } catch {
  } finally {
    submitting.value = false
  }
}

const handleReopen = async () => {
  if (submitting.value) return
  submitting.value = true
  try {
    await props.onReopen()
    reopenModal.close()
  } catch {
  } finally {
    submitting.value = false
  }
}

const handleReclose = async () => {
  if (submitting.value) return
  submitting.value = true
  try {
    await props.onReclose()
    recloseModal.close()
  } catch {
  } finally {
    submitting.value = false
  }
}
</script>

<template>
  <EntityLayout selectedTab="overview" :skeleton="skeleton">
    <template v-if="!skeleton" #actions>
      <div class="relative" ref="stageMenuRef">
        <button
          class="flex cursor-pointer items-center rounded-full border-none bg-white py-[9px] pl-[14px] pr-[10px] text-[13px] leading-none shadow-sm ring-1 ring-gray-700/20 transition-all duration-150 ease-[cubic-bezier(0.22,1,0.36,1)] hover:shadow-md hover:ring-gray-700/30 active:translate-y-px active:shadow-sm"
          :style="{ fontFeatureSettings: 'tnum', fontVariantNumeric: 'tabular-nums' }"
          data-testid="lifecycle-stage-pill"
          @click="stageMenuOpen = !stageMenuOpen"
        >
          <span
            class="mr-1.5 h-1.5 w-1.5 rounded-full"
            :class="lifecycleState === 'exited' ? 'bg-teal-600' : 'bg-sky-600'"
          />
          <span class="font-medium text-gray-700">{{ stageLabel }}</span>
          <ChevronDown
            :size="12"
            class="ml-1 text-gray-400 transition-transform duration-150"
            :class="{ 'rotate-180': stageMenuOpen }"
          />
        </button>
        <div
          v-if="stageMenuOpen"
          class="absolute right-0 top-[calc(100%+8px)] z-[60] min-w-[180px] origin-top-right rounded-xl bg-white p-1.5 shadow-[0_4px_24px_rgba(0,0,0,0.08),0_0_0_1px_rgba(0,0,0,0.06)]"
        >
          <button
            v-for="(s, idx) in stages"
            :key="s.key"
            class="flex w-full items-center gap-2.5 rounded-lg border-none bg-transparent px-3 py-2 text-left text-[13px] font-medium transition-colors duration-100"
            :class="[
              canSelectStage(s.key) ? 'cursor-pointer hover:bg-gray-100 active:bg-gray-200' : '',
              !canSelectStage(s.key) && s.key !== lifecycleState ? 'cursor-not-allowed opacity-40' : '',
              s.key === lifecycleState
                ? lifecycleState === 'exited'
                  ? 'bg-teal-600/10 font-semibold text-teal-600'
                  : 'bg-sky-600/10 font-semibold text-sky-600'
                : 'text-gray-700',
            ]"
            :disabled="!canSelectStage(s.key) && s.key !== lifecycleState"
            :data-testid="`lifecycle-stage-option-${s.key}`"
            @click="selectStage(s.key)"
          >
            <span
              class="flex h-[22px] w-[22px] flex-shrink-0 items-center justify-center rounded-full text-[11px] font-semibold"
              :class="
                isStageCompleted(idx)
                  ? 'bg-teal-600 text-white'
                  : isStageActive(idx)
                    ? 'bg-sky-600 text-white'
                    : 'bg-gray-100 text-gray-500'
              "
            >
              <Check v-if="isStageCompleted(idx)" :size="11" />
              <span v-else>{{ idx + 1 }}</span>
            </span>
            {{ s.label }}
          </button>
        </div>
      </div>
      <RouterLink v-if="isAdmin" :to="editRoute">
        <VButton size="md" variant="secondary">Edit</VButton>
      </RouterLink>
    </template>

    <OverviewSkeleton v-if="skeleton" />
    <slot v-else />
  </EntityLayout>

  <CloseSlideOut
    v-if="entityType !== 'fund'"
    :modalStore="closeSlideOut"
    :entityType="entityType"
    :fetchCloseReadiness="fetchCloseReadiness"
    :completeInvestmentHref="completeInvestmentHref"
    :onSubmit="onCloseSubmit"
  />

  <VConfirmDialog
    :modalStore="closeFundModal"
    title="Close Raising"
    :message="`This will close raising for the ${entityLabel.toLowerCase()}. New ${investorLabel} will no longer be able to commit.`"
    confirmLabel="Close Raising"
    :loading="submitting"
    @confirm="handleCloseFund"
  />
  <ExitSlideOut
    :modalStore="exitSlideOut"
    :entityType="entityType"
    :fetchExitReadiness="fetchExitReadiness"
    :onSubmit="onExitSubmit"
  />

  <VConfirmDialog
    :modalStore="reopenModal"
    :title="`Reopen ${entityLabel}`"
    :message="`This will reopen the ${entityLabel.toLowerCase()} so new ${investorLabel} can commit.`"
    :confirmLabel="`Reopen ${entityLabel}`"
    :loading="submitting"
    @confirm="handleReopen"
  />

  <VConfirmDialog
    :modalStore="recloseModal"
    :title="`Re-close ${entityLabel}`"
    :message="`This will reverse the exit and return the ${entityLabel.toLowerCase()} to closed status. Exit details (date, type, documents) are preserved as historical reference.`"
    :confirmLabel="`Re-close ${entityLabel}`"
    :loading="submitting"
    @confirm="handleReclose"
  />
</template>
